import React from 'react';
import PropTypes from 'prop-types';
import { DayPicker } from 'react-day-picker';
import {
  compareAsc,
  addDays,
  parseISO,
  isBefore,
  startOfToday,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { Oval } from 'react-loader-spinner';
import 'react-day-picker/src/style.css';
import './css/DayPickerComponent.css';
import { connect } from 'react-redux';
import { getTourDates } from '../../../Utils/RestUtils';
import {
  changeDepartureDate,
  changeCalendarSelected,
  changeAvailableDays,
  changeAvailableDaysKey,
} from '../../../DataStore/actions/searchFormActions';
import {
  changeOnlyCharter,
  changeDirectFlight,
} from '../../../DataStore/actions/FiltersAction';
import {
  CHARTER_ONLY,
  DIRECT_FLIGHT_FULL,
  AVAILABLE_DATES_HINT,
} from '../../../Utils/Constants/GlobalConstants';
import { ReactComponent as DefaultCheck } from '../../../images/default-check-r.svg';
import { ReactComponent as ActiveCheck } from '../../../images/active-check-r.svg';

const mapStateToProps = (state) => ({
  ...state,
});

const maxDayPeriod = 20;

// module-level: the calendar remounts on every open
let fetchDataCounter = 0;

function renderCheck(isActive) {
  if (isActive) {
    return <ActiveCheck className="dp_check" />;
  }

  return <DefaultCheck className="dp_check" />;
}

class DayPickerComponent extends React.Component {
  constructor(props) {
    super(props);
    this.handleDayClick = this.handleDayClick.bind(this);
    this.state = { isLoading: false };
  }

  componentDidMount() {
    this.fetchAvailableDays();
  }

  componentDidUpdate(prevProps) {
    const { onlyCharter, directFlight } = this.props;

    if (
      prevProps.onlyCharter !== onlyCharter ||
      prevProps.directFlight !== directFlight
    ) {
      this.fetchAvailableDays();
    }
  }

  handleDayClick(day) {
    const {
      changeDepartureDate: changeDepartureDateFunc,
      changeCalendarSelected: changeCalendarSelectedFunc,
      departureDate,
    } = this.props;

    if (departureDate.from && departureDate.to) {
      changeDepartureDateFunc({ from: day, to: null });
      changeCalendarSelectedFunc({ from: day, to: null });
    } else if (departureDate.from && !departureDate.to) {
      if (compareAsc(departureDate.from, day) === 1) {
        changeDepartureDateFunc({ from: day, to: departureDate.from });
      } else {
        const lastDay = addDays(departureDate.from, maxDayPeriod);

        if (compareAsc(day, lastDay) <= 0) {
          changeDepartureDateFunc({ from: departureDate.from, to: day });
        } else {
          changeDepartureDateFunc({ from: departureDate.from, to: lastDay });
        }
      }
    }
  }

  setHovered = (day) => {
    const {
      changeCalendarSelected: changeCalendarSelectedFunc,
      departureDate,
    } = this.props;

    if (departureDate.from && !departureDate.to) {
      if (compareAsc(departureDate.from, day) === 0) {
        changeCalendarSelectedFunc({ from: departureDate.from, to: day });
      } else if (compareAsc(departureDate.from, day) < 0) {
        const lastDay = addDays(departureDate.from, maxDayPeriod);

        if (compareAsc(day, lastDay) <= 0) {
          changeCalendarSelectedFunc({ from: departureDate.from, to: day });
        } else {
          changeCalendarSelectedFunc({ from: departureDate.from, to: lastDay });
        }
      } else {
        const lastDay = addDays(departureDate.from, -maxDayPeriod);

        if (compareAsc(day, lastDay) >= 0) {
          changeCalendarSelectedFunc({ from: day, to: departureDate.from });
        } else {
          changeCalendarSelectedFunc({ from: lastDay, to: departureDate.from });
        }
      }
    }
  };

  fetchAvailableDays = () => {
    const {
      depatureCity,
      destination,
      onlyCharter,
      directFlight,
      availableDaysKey,
      changeAvailableDays: changeAvailableDaysAction,
      changeAvailableDaysKey: changeAvailableDaysKeyAction,
    } = this.props;

    if (!depatureCity || !destination) {
      return;
    }

    const key = `${depatureCity.id}|${destination.id}|${onlyCharter}|${directFlight}`;

    if (key === availableDaysKey) {
      return;
    }

    fetchDataCounter += 1;
    const local = fetchDataCounter;

    // don't show dates of the previous city/country while loading
    changeAvailableDaysAction([]);
    this.setState({ isLoading: true });

    getTourDates(
      depatureCity.id,
      destination.id,
      undefined,
      onlyCharter === 1 ? true : undefined,
      directFlight === 1 ? true : undefined,
    )
      .then((response) => {
        if (local !== fetchDataCounter) {
          return;
        }

        if (response && Array.isArray(response.data)) {
          const today = startOfToday();
          const days = response.data
            .map((d) => parseISO(d))
            .filter((d) => !isBefore(d, today));

          changeAvailableDaysAction(days);
          changeAvailableDaysKeyAction(key);
        }

        this.setState({ isLoading: false });
      })
      .catch(() => {
        if (local === fetchDataCounter) {
          changeAvailableDaysAction([]);
          this.setState({ isLoading: false });
        }
      });
  };

  handleCharterChange = () => {
    const { onlyCharter, changeOnlyCharter: changeOnlyCharterAction } =
      this.props;

    changeOnlyCharterAction(onlyCharter === 1 ? 0 : 1);
  };

  handleDirectFlightChange = () => {
    const { directFlight, changeDirectFlight: changeDirectFlightAction } =
      this.props;

    changeDirectFlightAction(directFlight === 1 ? 0 : 1);
  };

  render() {
    const { calendarSelected, availableDays, onlyCharter, directFlight } =
      this.props;
    const { isLoading } = this.state;

    return (
      <div>
        <DayPicker
          mode="range"
          numberOfMonths={2}
          locale={ru}
          onDayClick={this.handleDayClick}
          disabled={[{ before: new Date() }]}
          selected={calendarSelected}
          onDayMouseEnter={this.setHovered}
          modifiers={{ available: availableDays }}
          modifiersClassNames={{ available: 'dp_available_day' }}
        />
        <div className="dp_footer">
          <div className="dp_hint gr_text_14">
            <span className="dp_hint_dot" />
            {AVAILABLE_DATES_HINT}
            <Oval
              visible={isLoading}
              height="14"
              width="14"
              color="#00BFFF"
              secondaryColor="#D9F5FF"
              strokeWidth="5"
              strokeWidthSecondary="5"
              ariaLabel="oval-loading"
              wrapperClass="dp_hint_spinner"
            />
          </div>
          <div className="dp_filters">
            <button
              type="button"
              className="dp_filter br_text_14"
              onClick={this.handleDirectFlightChange}
            >
              {renderCheck(directFlight === 1)}
              {DIRECT_FLIGHT_FULL}
            </button>
            <button
              type="button"
              className="dp_filter br_text_14"
              onClick={this.handleCharterChange}
            >
              {renderCheck(onlyCharter === 1)}
              {CHARTER_ONLY}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

DayPickerComponent.propTypes = {
  changeDepartureDate: PropTypes.func.isRequired,
  changeCalendarSelected: PropTypes.func.isRequired,
  departureDate: PropTypes.instanceOf(Object).isRequired,
  calendarSelected: PropTypes.instanceOf(Object).isRequired,
  changeAvailableDays: PropTypes.func.isRequired,
  changeAvailableDaysKey: PropTypes.func.isRequired,
  depatureCity: PropTypes.instanceOf(Object).isRequired,
  destination: PropTypes.instanceOf(Object).isRequired,
  availableDays: PropTypes.instanceOf(Array).isRequired,
  availableDaysKey: PropTypes.string.isRequired,
  onlyCharter: PropTypes.number.isRequired,
  directFlight: PropTypes.number.isRequired,
  changeOnlyCharter: PropTypes.func.isRequired,
  changeDirectFlight: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, {
  changeDepartureDate,
  changeCalendarSelected,
  changeAvailableDays,
  changeAvailableDaysKey,
  changeOnlyCharter,
  changeDirectFlight,
})(DayPickerComponent);
